import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Grid,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from "@/api/client";

const RetreatView = () => {
    const [retreats, setRetreats] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [editingRetreat, setEditingRetreat] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        request_for_retreat: '',
        retreat_no: '',
        course_type: '',
        start_date: '',
        end_date: '',
        status: '',
        teachers_name: '',
        organiser_contact_no: '',
        notes: '',
    });

    const courseTypes = [
        'Meditation',
        'Mindfulness',
        'Vipassana',
        'Zen',
        'Loving-kindness',
        'Walking Meditation',
        'Silent Retreat',
        'Dharma Study',
        'Contemplative Practice'
    ];

    const statusOptions = [
        'Scheduled',
        'In Progress',
        'Completed',
        'Cancelled',
        'Postponed'
    ];



    useEffect(() => {
        fetchRetreats();
    }, []);

    const fetchRetreats = async () => {
        try {
            setIsTableLoading(true);

            // Simulate minimum loading time
            setTimeout(() => {
            setIsTableLoading(false);
            }, 1000);

            const response = await apiClient.get('/get-internal-retreats');
            const result = response.data;
            setRetreats(result.data || result);
        } catch (error) {
            console.error('Error fetching retreats:', error);
            setIsTableLoading(false);
        }
    };

    const generateRetreatNumber = () => {
        const year = new Date().getFullYear();
        const count = retreats.length + 1;
        return `RET-${year}-${String(count).padStart(3, '0')}`;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!formData.request_for_retreat.trim()) {
            tempErrors.request_for_retreat = "Retreat name is required";
            isValid = false;
        }

        if (!formData.course_type) {
            tempErrors.course_type = "Course type is required";
            isValid = false;
        }

        if (!formData.start_date) {
            tempErrors.start_date = "Start date is required";
            isValid = false;
        }

        if (!formData.end_date) {
            tempErrors.end_date = "End date is required";
            isValid = false;
        } else if (new Date(formData.start_date) > new Date(formData.end_date)) {
            tempErrors.end_date = "End date must be after start date";
            isValid = false;
        }

        if (!formData.status) {
            tempErrors.status = "Status is required";
            isValid = false;
        }

        if (!formData.teachers_name.trim()) {
            tempErrors.teachers_name = "Teacher's name is required";
            isValid = false;
        }

        if (!formData.organiser_contact_no.trim()) {
            tempErrors.organiser_contact_no = "Organizer contact is required";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const resetForm = () => {
        setFormData({
            request_for_retreat: '',
            retreat_no: '',
            course_type: '',
            start_date: '',
            end_date: '',
            status: '',
            teachers_name: '',
            organiser_contact_no: '',
            notes: '',
        });
        setErrors({});
        setEditingRetreat(null);
    };

    const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    setIsLoading(true);
    try {
        // For new retreats, generate retreat number
        const submitData = {
        ...formData,
        retreat_no: editingRetreat ? formData.retreat_no : generateRetreatNumber(),
        };

        // Simulate API call
        setTimeout(() => {
        if (editingRetreat) {
            setRetreats(retreats.map(retreat => 
            retreat.id === editingRetreat.id 
                ? { ...retreat, ...submitData }
                : retreat
            ));
            alert('Retreat updated successfully!');
        } else {
            const newRetreat = {
            id: retreats.length + 1,
            ...submitData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            deleted_at: null
            };
            setRetreats([newRetreat, ...retreats]);
            alert('Retreat created successfully!');
        }
        
        resetForm();
        setOpenDialog(false);
        setIsLoading(false);
        }, 1000);

        // Actual API call using apiClient
        const url = editingRetreat 
        ? `/update-internal-retreats/${editingRetreat.id}`
        : '/create-internal-retreats';
        const method = editingRetreat ? 'PUT' : 'POST';

        let response;
        if (method === 'PUT') {
        response = await apiClient.put(url, submitData);
        } else {
        response = await apiClient.post(url, submitData);
        }
        const result = response.data; // Axios response data

        // If we reach here, request succeeded (Axios throws on error)
        alert(`Retreat ${editingRetreat ? 'updated' : 'created'} successfully!`);
        resetForm();
        setOpenDialog(false);
        fetchRetreats();

    } catch (error) {
        console.error('Error:', error);
        let errorMsg = error.message;
        if (error.response?.data?.errors) {
        errorMsg = Object.values(error.response.data.errors).flat().join('\n');
        } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
        }
        alert(`Error: ${errorMsg}`);
        setIsLoading(false);
    }
    };

    const handleEdit = (retreat) => {
        setEditingRetreat(retreat);
        setFormData({
            request_for_retreat: retreat.request_for_retreat || '',
            retreat_no: retreat.retreat_no || '',
            course_type: retreat.course_type || '',
            start_date: retreat.start_date ? retreat.start_date.split('T')[0] : '',
            end_date: retreat.end_date ? retreat.end_date.split('T')[0] : '',
            status: retreat.status || '',
            teachers_name: retreat.teachers_name || '',
            organiser_contact_no: retreat.organiser_contact_no || '',
            notes: retreat.notes || '',
        });
        setOpenDialog(true);
    };

    const handleDelete = async (retreatId) => {
    const confirmed = window.confirm('Are you sure you want to delete this retreat?');

    if (confirmed) {
        try {
        // Simulate API call
        setRetreats(retreats.filter(retreat => retreat.id !== retreatId));
        alert('Retreat deleted successfully!');

        // Actual API call using apiClient
        await apiClient.delete(`/delete-internal-retreats/${retreatId}`);

        // Refresh data from server to ensure consistency
        fetchRetreats();
        } catch (error) {
        console.error('Error:', error);
        // Revert optimistic update? (optional, but original didn't)
        alert('Failed to delete retreat.');
        // Optionally re-fetch to restore correct data
        fetchRetreats();
        }
    }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'scheduled': return 'primary';
            case 'in progress': return 'warning';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            case 'postponed': return 'default';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ minWidth: 275 }}>
            {/* Create Retreat Button */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" component="div">
                            Retreat Management
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetForm();
                                setOpenDialog(true);
                            }}
                        >
                            Create New Retreat
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Retreats Table */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        All Retreats
                    </Typography>
                    <hr />
                    {isTableLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Retreat Details</TableCell>
                                        <TableCell>Course Type</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Teacher</TableCell>
                                        <TableCell>Contact</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {retreats.length > 0 ? (
                                        retreats.map((retreat) => (
                                            <TableRow key={retreat.id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {retreat.request_for_retreat}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {retreat.retreat_no}
                                                    </Typography>
                                                    {retreat.notes && (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {retreat.notes.length > 30
                                                                ? `${retreat.notes.substring(0, 30)}...`
                                                                : retreat.notes}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={retreat.course_type} size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell>{formatDate(retreat.start_date)}</TableCell>
                                                <TableCell>{formatDate(retreat.end_date)}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={retreat.status} 
                                                        size="small" 
                                                        color={getStatusColor(retreat.status)}
                                                    />
                                                </TableCell>
                                                <TableCell>{retreat.teachers_name}</TableCell>
                                                <TableCell>{retreat.organiser_contact_no}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleEdit(retreat)}
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleDelete(retreat.id)}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No retreats found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Retreat Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingRetreat ? 'Edit Retreat' : 'Create New Retreat'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="request_for_retreat"
                                label="Retreat Name"
                                placeholder="Enter retreat name"
                                variant="outlined"
                                value={formData.request_for_retreat}
                                onChange={handleChange}
                                error={!!errors.request_for_retreat}
                                helperText={errors.request_for_retreat}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="retreat_no"
                                label="Retreat Number"
                                placeholder="Auto-generated"
                                variant="outlined"
                                value={formData.retreat_no}
                                onChange={handleChange}
                                disabled={!editingRetreat}
                                helperText="Auto-generated for new retreats"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!errors.course_type}>
                                <InputLabel id="course-type-label">Course Type</InputLabel>
                                <Select
                                    labelId="course-type-label"
                                    name="course_type"
                                    value={formData.course_type}
                                    onChange={handleChange}
                                    label="Course Type"
                                    required
                                >
                                    {courseTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.course_type && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.course_type}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!errors.status}>
                                <InputLabel id="status-label">Status</InputLabel>
                                <Select
                                    labelId="status-label"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    label="Status"
                                    required
                                >
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.status && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.status}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="start_date"
                                label="Start Date"
                                type="date"
                                variant="outlined"
                                value={formData.start_date}
                                onChange={handleChange}
                                error={!!errors.start_date}
                                helperText={errors.start_date}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="end_date"
                                label="End Date"
                                type="date"
                                variant="outlined"
                                value={formData.end_date}
                                onChange={handleChange}
                                error={!!errors.end_date}
                                helperText={errors.end_date}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="teachers_name"
                                label="Teacher's Name"
                                placeholder="Enter teacher's name"
                                variant="outlined"
                                value={formData.teachers_name}
                                onChange={handleChange}
                                error={!!errors.teachers_name}
                                helperText={errors.teachers_name}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="organiser_contact_no"
                                label="Organizer Contact"
                                placeholder="Enter contact number"
                                variant="outlined"
                                value={formData.organiser_contact_no}
                                onChange={handleChange}
                                error={!!errors.organiser_contact_no}
                                helperText={errors.organiser_contact_no}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="notes"
                                label="Notes (Optional)"
                                placeholder="Enter any additional notes"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={formData.notes}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {isLoading ? (editingRetreat ? 'Updating...' : 'Creating...') : (editingRetreat ? 'Update Retreat' : 'Create Retreat')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RetreatView;